import { DASHBOARD_MAIN_NAV } from './dashboardNav'
import { LEGACY_PAGE_KEY_CLIENTS, resolvePageKey } from './permissions'

const DASHBOARD_TAB_IDS = new Set(DASHBOARD_MAIN_NAV.filter((n) => !n.href).map((n) => n.id))

/** @deprecated Legacy tab param */
const LEGACY_TAB_ALIASES = new Set([LEGACY_PAGE_KEY_CLIENTS])

/**
 * @param {string} pathname
 * @param {string | null} tabParam — `tab` search param on `/`
 */
export function getMainSidebarActiveNavId(pathname, tabParam) {
  if (pathname === '/profile') return 'profile'
  if (pathname === '/agents') return 'agents'
  if (pathname === '/knowledge' || pathname === '/admin/knowledge/search') return 'knowledge-search'
  if (pathname === '/personal-knowledge') return 'personal-knowledge'
  if (pathname.startsWith('/race-tracker')) return 'race-tracker'
  if (pathname === '/') {
    if (!tabParam) return 'platform-overview'
    const normalized = LEGACY_TAB_ALIASES.has(tabParam) ? 'clients' : tabParam
    if (DASHBOARD_TAB_IDS.has(normalized)) return normalized
    return 'platform-overview'
  }
  return 'platform-overview'
}

/**
 * @param {string} pathname
 */
export function getAdminSidebarActiveNavId(pathname, search = '') {
  const params = new URLSearchParams(search)
  const tab = params.get('tab')
  if (pathname === '/admin/knowledge/access') {
    if (tab === 'playground') return 'kb-dev-playground'
    if (tab === 'settings') return 'kb-dev-settings'
    if (tab === 'eval') return 'kb-dev-eval'
    if (tab === 'latency') return 'kb-dev-latency'
    return 'knowledge-base:access'
  }
  if (pathname === '/admin/knowledge/dashboard' || pathname === '/admin/knowledge') {
    return 'knowledge-base:dashboard'
  }
  if (pathname === '/admin/knowledge/content') return 'knowledge-base:content'
  if (pathname === '/admin/knowledge/wiki') return 'knowledge-base:wiki'
  if (pathname === '/admin/mcp/overview') return 'mcp-server:overview'
  if (pathname === '/admin/mcp/keys') return 'mcp-server:keys'
  if (pathname === '/admin/mcp/tools') return 'mcp-server:tools'
  if (pathname === '/admin/mcp/logs') return 'mcp-server:logs'
  if (pathname === '/admin/users' || pathname === '/users') return 'settings-users'
  if (pathname === '/admin/integrations' || pathname === '/integrations') return 'settings-integrations'
  if (pathname === '/admin/basecamp-api-references' || pathname === '/basecamp-api-references')
    return 'basecamp-api-references'
  if (pathname === '/admin/technical-documentation' || pathname === '/technical-documentation')
    return 'technical-documentation'
  if (pathname === '/admin/basecamp/knowledge' || pathname === '/basecamp/knowledge') return 'basecamp-knowledge'
  if (pathname === '/admin/basecamp/projects' || pathname === '/basecamp/projects') return 'basecamp-projects'
  if (pathname === '/projects') return 'basecamp-projects'
  if (
    pathname === '/admin/agents-settings' ||
    pathname === '/admin/ai/agents' ||
    pathname === '/ai/agents'
  ) {
    return 'ai-agents'
  }
  if (pathname === '/admin/ai/agents-logs' || pathname === '/ai/agents-logs') return 'ai-agents-logs'
  if (pathname === '/admin/ai/cost-monitoring' || pathname === '/ai/cost-monitoring') return 'ai-cost-monitoring'
  if (
    pathname === '/admin/outlook' ||
    pathname.startsWith('/admin/outlook/') ||
    pathname === '/outlook/knowledge' ||
    pathname === '/outlook/read'
  ) {
    return 'outlook'
  }
  if (pathname === '/admin/sharepoint' || pathname.startsWith('/admin/sharepoint/')) {
    return 'sharepoints'
  }
  if (pathname === '/admin/onenote' || pathname.startsWith('/admin/onenote/')) {
    return 'onenotes'
  }
  return 'settings-users'
}

/** @deprecated Use getMainSidebarActiveNavId or getAdminSidebarActiveNavId */
export function getSidebarActiveNavId(pathname, tabParam) {
  if (pathname.startsWith('/admin')) return getAdminSidebarActiveNavId(pathname)
  return getMainSidebarActiveNavId(pathname, tabParam)
}

/**
 * @param {string} id — nav item id
 * @param {import('react-router-dom').NavigateFunction} navigate
 */
export function navigateFromMainSidebarItem(id, navigate) {
  if (id === 'race-tracker') {
    navigate('/race-tracker')
    return
  }
  if (id === 'platform-overview') {
    navigate('/', { replace: false })
    return
  }
  if (id === 'agents') {
    navigate('/agents')
    return
  }
  if (id === 'knowledge-search') {
    navigate('/knowledge')
    return
  }
  const tabId = resolvePageKey(id)
  navigate(`/?tab=${encodeURIComponent(tabId)}`)
}

/**
 * @param {string} id — admin nav item id
 * @param {import('react-router-dom').NavigateFunction} navigate
 */
export function navigateFromAdminSidebarItem(id, navigate) {
  if (id === 'knowledge-base:dashboard') {
    navigate('/admin/knowledge/dashboard')
    return
  }
  if (id === 'knowledge-base:content') {
    navigate('/admin/knowledge/content')
    return
  }
  if (id === 'knowledge-base:wiki') {
    navigate('/admin/knowledge/wiki')
    return
  }
  if (id === 'knowledge-base:access') {
    navigate('/admin/knowledge/access?tab=permissions')
    return
  }
  if (id === 'kb-dev-playground') {
    navigate('/admin/knowledge/access?tab=playground')
    return
  }
  if (id === 'kb-dev-settings') {
    navigate('/admin/knowledge/access?tab=settings')
    return
  }
  if (id === 'kb-dev-eval') {
    navigate('/admin/knowledge/access?tab=eval')
    return
  }
  if (id === 'kb-dev-latency') {
    navigate('/admin/knowledge/access?tab=latency')
    return
  }
  if (id === 'mcp-server:overview') {
    navigate('/admin/mcp/overview')
    return
  }
  if (id === 'mcp-server:keys') {
    navigate('/admin/mcp/keys')
    return
  }
  if (id === 'mcp-server:tools') {
    navigate('/admin/mcp/tools')
    return
  }
  if (id === 'mcp-server:logs') {
    navigate('/admin/mcp/logs')
    return
  }
  if (id === 'settings-users') {
    navigate('/admin/users')
    return
  }
  if (id === 'settings-integrations') {
    navigate('/admin/integrations')
    return
  }
  if (id === 'basecamp-api-references') {
    navigate('/admin/basecamp-api-references')
    return
  }
  if (id === 'technical-documentation') {
    navigate('/admin/technical-documentation')
    return
  }
  if (id === 'basecamp-knowledge') {
    navigate('/admin/basecamp/knowledge')
    return
  }
  if (id === 'basecamp-projects') {
    navigate('/admin/basecamp/projects')
    return
  }
  if (id === 'ai-agents') {
    navigate('/admin/agents-settings')
    return
  }
  if (id === 'ai-agents-logs') {
    navigate('/admin/ai/agents-logs')
    return
  }
  if (id === 'ai-cost-monitoring') {
    navigate('/admin/ai/cost-monitoring')
    return
  }
  if (id === 'outlook') {
    navigate('/admin/outlook')
    return
  }
  if (id === 'sharepoints') {
    navigate('/admin/sharepoint')
    return
  }
  if (id === 'onenotes') {
    navigate('/admin/onenote')
    return
  }
}

/**
 * @param {string} id — nav item id
 * @param {import('react-router-dom').NavigateFunction} navigate
 */
export function navigateFromSidebarItem(id, navigate) {
  if (id === 'race-tracker') {
    navigate('/race-tracker')
    return
  }
  const adminIds = new Set([
    'knowledge-base:dashboard',
    'knowledge-base:content',
    'knowledge-base:wiki',
    'knowledge-base:access',
    'kb-dev-playground',
    'kb-dev-settings',
    'kb-dev-eval',
    'kb-dev-latency',
    'mcp-server:overview',
    'mcp-server:keys',
    'mcp-server:tools',
    'mcp-server:logs',
    'settings-users',
    'settings-integrations',
    'basecamp-api-references',
    'technical-documentation',
    'basecamp-knowledge',
    'basecamp-projects',
    'ai-agents',
    'ai-agents-logs',
    'ai-cost-monitoring',
    'outlook',
    'sharepoints',
    'onenotes',
  ])
  if (adminIds.has(id)) {
    navigateFromAdminSidebarItem(id, navigate)
    return
  }
  navigateFromMainSidebarItem(id, navigate)
}
